/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Old Way vs FalcoCut comparison section for TikTok video creation
 */
import Link from 'next/link';
import React from 'react';

const oldWayItems: string[] = [
  'Spend hours scripting, filming, and editing each video',
  'Hire freelancers or agencies for every new campaign',
  'Manually add captions, music, and transitions',
  'Re-create content from scratch for each platform',
  'Struggle to keep up with trends and posting schedules',
];

const newWayItems: string[] = [
  'Generate TikTok-ready videos in minutes from a prompt or link',
  'Scale content production without extra headcount',
  'AI handles captions, voiceovers, music, and pacing automatically',
  'Repurpose one idea across TikTok, Reels, and Shorts instantly',
  'Stay on trend with AI-optimized formats and styles',
];

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

const ComparisonSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Old Way vs. FalcoCut AI TikTok Video Generator
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Traditional video creation is slow, expensive, and hard to scale. FalcoCut
          replaces the entire workflow with AI — giving you faster results, lower costs,
          and content that performs.
        </p>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-6 pad:gap-8 mb-10 pad:mb-14">
          {/* Old Way Card */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 pad:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">Old Way</h3>
            </div>

            {/* List */}
            <ul className="space-y-4 mb-6">
              {oldWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-gray-400 mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <div className="flex items-center gap-2 text-base font-semibold text-gray-800">
              <span>Slow, expensive, and hard to scale.</span>
            </div>
          </div>

          {/* New Way Card */}
          <div className="rounded-2xl border-2 border-[#6366F1] bg-[#6366F1]/5 p-6 pad:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5 4.5L6 12L2.5 8.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">
                New Way with FalcoCut
              </h3>
            </div>

            {/* List */}
            <ul className="space-y-4 mb-6">
              {newWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-[#6366F1] mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <div className="flex items-center gap-2 text-base font-semibold text-[#6366F1]">
              <span>Fast, scalable, and built for performance.</span>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <Link
            href="/ai-video-generator"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
            }}
          >
            <span>Create TikTok Videos</span>
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
